import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { cn } from '../../utils/cn';

/*
 * FormPrivacyNotice
 * -----------------
 * Small compliance / NDA reassurance line that sits at the very
 * bottom of every "Let's Connect" or RFP form on the site.
 *
 * Two visual variants:
 *   • `onLight` — white / card surfaces (Contact page card).
 *   • `onDark`  — navy backgrounds (global ConnectSection strip).
 *
 * The copy is intentionally centralised here so any future wording
 * change (legal review, tone tweak, translation) is a one-file edit
 * that automatically ripples through every form on the site.
 */

interface FormPrivacyNoticeProps {
  /** Which surface the notice sits on. Drives the text/icon colour. */
  tone?: 'onDark' | 'onLight';
  /** Extra classes for spacing tweaks per host form (e.g. `mt-6`). */
  className?: string;
}

export const FormPrivacyNotice: React.FC<FormPrivacyNoticeProps> = ({
  tone = 'onLight',
  className,
}) => {
  const isDark = tone === 'onDark';
  return (
    <p
      className={cn(
        // `flex items-start` so a small shield icon aligns with the
        // first line of the sentence, then the copy can wrap freely
        // beneath it on narrow screens without the icon jumping down.
        //
        // Colour intent: previous shades (`text-navy-500` / `text-white/55`)
        // read as legal-fine-print — too muted to be a reassurance the
        // visitor actually notices while filling the form. We now use
        // pure black on light surfaces and near-full white on dark
        // surfaces so the notice is clearly readable at 11 px without
        // introducing an alarming colour (red / yellow / brand-accent).
        // Text stays deliberately small (`text-[11px]`) so it still
        // reads as a footer note rather than competing with the
        // submit button above it.
        'mt-4 flex items-start gap-1.5 text-[11px] leading-relaxed font-medium',
        isDark ? 'text-white/95' : 'text-black',
        className,
      )}
    >
      <ShieldCheck
        className={cn(
          'w-3.5 h-3.5 shrink-0 mt-[1px]',
          isDark ? 'text-white/90' : 'text-black',
        )}
        aria-hidden
      />
      <span>
        Everything you share here is protected under strict IT
        compliance policies and mutual non-disclosure agreements —
        nothing is shared with anyone outside our team without your
        explicit approval.
      </span>
    </p>
  );
};
